import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { API, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/gtag";
import { BROCHURE_URL } from "@/data/apartments";
import { useLang } from "@/i18n";

export const openBrochureModal = () => window.dispatchEvent(new Event("dds-open-brochure"));

const EMPTY = { nome: "", cognome: "", email: "", telefono: "", privacy: false };

export const BrochureModal = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fn = () => setOpen(true);
    window.addEventListener("dds-open-brochure", fn);
    return () => window.removeEventListener("dds-open-brochure", fn);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        tipologia: "Brochure",
        messaggio: t.brochureModal.leadNote,
        lang,
      });
      track("download_brochure");
      toast.success(t.brochureModal.success);
      setOpen(false);
      setForm(EMPTY);
      const a = document.createElement("a");
      a.href = BROCHURE_URL;
      a.download = "Dimore-del-Sole-Brochure.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined} className="max-w-md border-line bg-ivory text-ink" data-testid="brochure-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-serif text-2xl font-medium text-ink">
            <Download className="h-5 w-5 text-terracotta" strokeWidth={1.5} />
            {t.brochureModal.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm leading-relaxed text-[#4A4F4C]">{t.brochureModal.subtitle}</p>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="brochure-form">
          <div className="grid grid-cols-2 gap-4">
            <Input required placeholder={t.contact.nome} value={form.nome} onChange={set("nome")} className="border-line bg-white" data-testid="brochure-nome" />
            <Input required placeholder={t.contact.cognome} value={form.cognome} onChange={set("cognome")} className="border-line bg-white" data-testid="brochure-cognome" />
          </div>
          <Input required type="email" placeholder={t.contact.email} value={form.email} onChange={set("email")} className="border-line bg-white" data-testid="brochure-email" />
          <Input required type="tel" placeholder={t.contact.telefono} value={form.telefono} onChange={set("telefono")} className="border-line bg-white" data-testid="brochure-telefono" />
          <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#6A6F6C]">
            <Checkbox
              checked={form.privacy}
              onCheckedChange={(v) => setForm((f) => ({ ...f, privacy: !!v }))}
              className="mt-0.5 border-ink/30 data-[state=checked]:border-terracotta data-[state=checked]:bg-terracotta"
              data-testid="brochure-privacy"
            />
            {t.contact.privacy}
          </label>
          <button
            type="submit"
            disabled={sending || !form.privacy}
            data-testid="brochure-submit"
            className="w-full rounded-full bg-terracotta px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory shadow-lg transition-colors duration-300 hover:bg-sun hover:text-ink disabled:cursor-not-allowed disabled:bg-line disabled:text-ink/40"
          >
            {sending ? t.contact.sending : t.brochureModal.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
